// @flow

import * as React from 'react';
import WindowResize from '@ta/events.window-resize';
import WindowScroll from '@ta/events.window-scroll';
import { CloseIcon as CloseX } from '@ta/modal.core';
import { Overlay } from '@ta/overlays.pieces';
import styles from './GeoPillRedCarpetContents.less';

type Props = {|
  /** Transition statuses as defined by react-transition-group */
  transitionStatus: 'entering' | 'entered' | 'exiting' | 'exited',

  /** The target for which we animate from */
  target: ?HTMLElement,

  close: () => void,

  /** Contents of the overlay */
  overlayContents: React.Node,
|};

type State = {|
  /** Current CSS height of the overlay */
  height: string,

  /**
   * Questionable tactic used to set an initial value on the height so we see an animation.
   * 1) This var is originally false.
   * 2) Upon props.transitionStatus being set to "exiting," we set state.readytoShrink to true and state.height to
   *    the current clientHeight of the overlay so that the transition can start at some initial value (rather than
   *    'auto').
   * 3) Because we've updated the state, componentDidUpdate() runs again and now that readyToShrink===true we
   *    know we can set the height back to the exiting value (EXIT_HEIGHT) and get the animation.
   * 4) Set readyToShrink back to false.
   */
  readyToShrink: boolean,

  /** Keep track of whether we've called props.close() yet so we don't call it multiple times from the scroll continuing through the exit animation. */
  closing: boolean,

  /** Need to keep offsetLeft in state because on resize it should adjust. */
  offsetLeft: string,

  /** Need to keep offsetRight in state because on resize it should adjust. */
  offsetRight: string,
|};

const HEIGHT_TRANSITION_TIMING = 300; // ms
const WIDENING_TRANSITION_TIMING = 150; // ms
const EXIT_HEIGHT = 74; // px
const END_WIDTH = 500; // px
const TOP_OFFSET = 150; // px
const HEADER_BUFFER = 100; // When user scrolls we want the overlay to exit right around the time it passes the global nav.

/**
 * The contents of the GeoPillRedCarpet overlay. It's a separate component because we need to add in some height transitioning based off
 * the transitionStatus prop. We use componentDidUpdate to check to see if we've gone from exited to entering, etc. so that we can do
 * the "first widen then grow in height" transition.
 */
class GeoPillRedCarpetContents extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // eslint-disable-next-line no-restricted-globals
    this.bodyEl = document && document.documentElement ? document.documentElement : null;

    // Initial offsetLeft/offsetRight can't be auto because the animation won't happen
    const initialOffsetLeft = '50vw';
    const initialOffsetRight = '50vw';

    this.state = {
      height: `${EXIT_HEIGHT}px`,
      readyToShrink: false,
      closing: false,
      offsetLeft: initialOffsetLeft,
      offsetRight: initialOffsetRight,
    };
    this.innerContentsRef = React.createRef();
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const contentsEl = this.innerContentsRef.current;

    // If we just started entering, kickoff a timeout to start the height transition.
    if (
      prevProps.transitionStatus === 'exited' &&
      this.props.transitionStatus === 'entering' &&
      contentsEl
    ) {
      this.setLeftRightOffsetState();
      this.timer = setTimeout(() => {
        this.setState({ height: `${contentsEl.clientHeight}px` });
      }, HEIGHT_TRANSITION_TIMING);
    }

    // Upon finishing the entering transition, set height to auto so that the overlay can overflow naturally.
    if (prevProps.transitionStatus === 'entering' && this.props.transitionStatus === 'entered') {
      // Bypassing the rule to allow for a two-part animation. Overlays cannot support height animations without React reparenting support.
      // No concerns on infinite loops here, though.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ height: 'auto' });
    }

    // If we are just beginning to exit we set state.readyToShrink to true and the height to the clientHeight which acts as the initial value
    // in the transition. Then, when componentDidUpdate gets called again we set the height to the exiting height below and get an animation.
    if (
      prevProps.transitionStatus === 'entered' &&
      this.props.transitionStatus === 'exiting' &&
      !prevState.readyToShrink &&
      contentsEl
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ height: `${contentsEl.clientHeight}px`, readyToShrink: true });
      this.setLeftRightOffsetState();
    }

    // Please see above comment on readytoShrink.
    if (this.state.readyToShrink && this.props.transitionStatus === 'exiting') {
      if (this.state.readyToShrink && !prevState.readyToShrink) {
        // Bypassing the rule to allow for a two-part animation. Overlays cannot support height animations without React reparenting support.
        // No concerns on infinite loops here, though.

        // Put the set state on the next tick to ensure the state change happens on separate commits
        this.shrinkTimeout = setTimeout(() => {
          this.setState({ height: `${EXIT_HEIGHT}px`, readyToShrink: false });
        }, 0);
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;

    // Avoid console errors if setTimeout tries to set state after dismount.
    if (this.timer) {
      clearTimeout(this.timer);
    }
    if (this.shrinkTimeout) {
      clearTimeout(this.shrinkTimeout);
    }
  }

  setLeftRightOffsetState() {
    const { offsetLeft, offsetRight } = this.getLeftRightOffset();
    this.setState({ offsetLeft, offsetRight });
  }

  getLeftRightOffset() {
    // eslint-disable-next-line no-restricted-globals
    if (!this.props.target || !document || !document.documentElement) {
      return { offsetLeft: 'auto', offsetRight: 'auto' };
    }

    // eslint-disable-next-line no-restricted-globals
    const body = document.documentElement;
    const { target, transitionStatus } = this.props;
    const rect = target.getBoundingClientRect();
    if (transitionStatus === 'exited' || transitionStatus === 'exiting') {
      const { left } = rect;
      const right = body.clientWidth - target.clientWidth - rect.left;
      return {
        offsetLeft: `${left}px`,
        offsetRight: `${right}px`,
      };
    }

    const gutter = body.clientWidth / 2 - END_WIDTH / 2;
    return {
      offsetLeft: `${gutter}px`,
      offsetRight: `${gutter}px`,
    };
  }

  /**
   * @param close Function that actually closes the overlay.
   * @param showX We don't show X right away because it looks weird during the widening animation where there's just a floating X on a backdrop for a moment.
   */
  overlayContent = (close: () => void, showX: boolean) => (
    <div
      className={styles.overlayContents}
      style={{ height: this.state.height, minHeight: EXIT_HEIGHT }}
    >
      <div ref={this.innerContentsRef}>
        {this.props.overlayContents}
        {showX && <CloseX onClick={close} testAttribute="geopill-red-carpet-close" />}
      </div>
    </div>
  );

  overlayProps = (transitionStatus: string, target: ?HTMLElement) => {
    // eslint-disable-next-line no-restricted-globals
    if (!target || !document || !document.documentElement) {
      return {
        position: {},
      };
    }

    const transitionProps = {
      transitionProperties: [
        {
          property: 'right',
          timing: WIDENING_TRANSITION_TIMING,
          delay:
            transitionStatus === 'exiting' || transitionStatus === 'entered'
              ? HEIGHT_TRANSITION_TIMING
              : 0,
        },
        {
          property: 'left',
          timing: WIDENING_TRANSITION_TIMING,
          delay:
            transitionStatus === 'exiting' || transitionStatus === 'entered'
              ? HEIGHT_TRANSITION_TIMING
              : 0,
        },
        {
          property: 'opacity',
          timing: WIDENING_TRANSITION_TIMING,
          delay:
            transitionStatus === 'exiting' || transitionStatus === 'entered'
              ? HEIGHT_TRANSITION_TIMING
              : 0,
        },
      ],
    };

    if (transitionStatus === 'exited' || transitionStatus === 'exiting') {
      return {
        ...transitionProps,
        opacity: '0',
        position: {
          position: 'absolute',
          top: `${TOP_OFFSET}px`,
          left: this.state.offsetLeft,
          right: this.state.offsetRight,
          transform: 'translate(0%, 0) scale(1, 1)',
        },
      };
    }

    return {
      ...transitionProps,
      opacity: '1',
      position: {
        position: 'absolute',
        top: `${TOP_OFFSET}px`,
        left: this.state.offsetLeft,
        right: this.state.offsetRight,
        transform: 'translate(0%, 0) scale(1, 1)',
      },
    };
  };

  checkIfScrolledAway() {
    if (!this.mounted) {
      return;
    }

    const contentsEl = this.innerContentsRef.current;
    /* eslint-disable no-restricted-globals */
    const body = document.documentElement;
    if (this.state.closing || !contentsEl || !body) {
      return;
    }

    // If we've scrolled a bit far past the overlay
    if (
      window.pageYOffset >
      contentsEl.getBoundingClientRect().top -
        body.getBoundingClientRect().top +
        contentsEl.offsetHeight -
        HEADER_BUFFER
    ) {
      this.props.close();
      this.setState({ closing: true });
    }
    /* eslint-enable no-restricted-globals */
  }

  innerContentsRef: any;
  timer: TimeoutID;
  shrinkTimeout: TimeoutID;
  mounted: boolean = false;
  bodyEl: ?HTMLElement;

  render() {
    return (
      <React.Fragment>
        <Overlay {...this.overlayProps(this.props.transitionStatus, this.props.target)}>
          {this.overlayContent(this.props.close, this.props.transitionStatus === 'entered')}
        </Overlay>
        <WindowResize callback={this.setLeftRightOffsetState} throttle={0} />
        <WindowScroll callback={this.checkIfScrolledAway} throttle={50} />
      </React.Fragment>
    );
  }
}

export default GeoPillRedCarpetContents;
